import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Edit2 } from "lucide-react";
import { useState, type ReactNode } from "react";

import {
  autoridades,
  motivosDescarte,
  motivosPausa,
  respuestas,
  tiposVehiculo,
  type Autoridad,
  type MotivoDescarte,
  type MotivoPausa,
  type Respuesta,
  type TipoVehiculo,
} from "@/api/catalogos";
import { useToast } from "@/contexts/ToastContext";

const TABS = [
  { id: "respuestas", label: "Respuestas" },
  { id: "autoridades", label: "Autoridades" },
  { id: "motivos-pausa", label: "Motivos de pausa" },
  { id: "motivos-descarte", label: "Motivos de descarte" },
  { id: "tipos-vehiculo", label: "Tipos de vehículo" },
] as const;

type TabId = (typeof TABS)[number]["id"];

export function CatalogosPage() {
  const [tab, setTab] = useState<TabId>("respuestas");

  return (
    <div className="p-6 space-y-4" data-testid="page-catalogos">
      <header>
        <h1 className="text-2xl font-semibold">Catálogos</h1>
      </header>
      <div role="tablist" className="flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`catalogo-tab-${t.id}`}
            className={
              "px-3 py-2 text-sm border-b-2 -mb-px " +
              (tab === t.id
                ? "border-primary text-primary-400"
                : "border-transparent text-muted-200 hover:text-foreground")
            }
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "respuestas" && <RespuestasTab />}
      {tab === "autoridades" && <AutoridadesTab />}
      {tab === "motivos-pausa" && <MotivosPausaTab />}
      {tab === "motivos-descarte" && <MotivosDescarteTab />}
      {tab === "tipos-vehiculo" && <TiposVehiculoTab />}
    </div>
  );
}

function RespuestasTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: ["respuestas"], queryFn: respuestas.list });
  const [editing, setEditing] = useState<Respuesta | null>(null);
  const [creating, setCreating] = useState(false);

  const remove = useMutation({
    mutationFn: (id: number) => respuestas.remove(id),
    onSuccess: () => {
      toast.push("Respuesta eliminada", "success");
      qc.invalidateQueries({ queryKey: ["respuestas"] });
    },
    onError: () => toast.push("Error al eliminar", "error"),
  });

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button
          className="btn-primary"
          data-testid="respuestas-new"
          onClick={() => setCreating(true)}
        >
          <Plus size={14} /> Nueva
        </button>
      </div>
      <div className="card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-200">
            <tr className="border-b border-border">
              <th className="py-2">Nombre</th>
              <th>Descripción</th>
              <th>Evidencia</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(data ?? []).map((r) => (
              <tr key={r.id} className="border-b border-border/40">
                <td className="py-2">{r.nombre}</td>
                <td>{r.descripcion}</td>
                <td>{r.requiereEvidencia ? "sí" : "no"}</td>
                <td className="space-x-1 text-right">
                  <button className="btn-ghost text-xs" onClick={() => setEditing(r)}>
                    <Edit2 size={12} /> Editar
                  </button>
                  <button
                    className="btn-ghost text-xs text-danger"
                    onClick={() => {
                      if (confirm(`¿Eliminar "${r.nombre}"?`)) remove.mutate(r.id);
                    }}
                  >
                    <Trash2 size={12} /> Eliminar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {creating && (
        <RespuestaFormDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ["respuestas"] });
          }}
        />
      )}
      {editing && (
        <RespuestaFormDialog
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["respuestas"] });
          }}
        />
      )}
    </div>
  );
}

function RespuestaFormDialog({
  row,
  onClose,
  onSaved,
}: {
  row?: Respuesta;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [nombre, setNombre] = useState(row?.nombre ?? "");
  const [descripcion, setDescripcion] = useState(row?.descripcion ?? "");
  const [reqEv, setReqEv] = useState(!!row?.requiereEvidencia);
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!nombre.trim()) return;
    setSubmitting(true);
    try {
      if (row) {
        await respuestas.update(row.id, {
          nombre: nombre.trim(),
          descripcion: descripcion || null,
          requiereEvidencia: reqEv,
        });
      } else {
        await respuestas.create({
          nombre: nombre.trim(),
          descripcion: descripcion || undefined,
          requiereEvidencia: reqEv,
        });
      }
      toast.push("Guardado", "success");
      onSaved();
    } catch {
      toast.push("Error al guardar", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title={row ? "Editar respuesta" : "Nueva respuesta"} onClose={onClose}>
      <input
        className="input"
        placeholder="Nombre"
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
        data-testid="respuesta-nombre"
      />
      <textarea
        className="input"
        rows={3}
        placeholder="Descripción"
        value={descripcion ?? ""}
        onChange={(e) => setDescripcion(e.target.value)}
      />
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={reqEv}
          onChange={(e) => setReqEv(e.target.checked)}
        />
        Requiere evidencia
      </label>
      <div className="flex gap-2">
        <button onClick={onClose} className="btn-ghost flex-1">Cancelar</button>
        <button
          onClick={submit}
          className="btn-primary flex-1"
          disabled={submitting || !nombre.trim()}
          data-testid="respuesta-submit"
        >
          Guardar
        </button>
      </div>
    </Modal>
  );
}

function AutoridadesTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["autoridades"], queryFn: autoridades.list });
  const [editing, setEditing] = useState<Autoridad | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button
          className="btn-primary"
          onClick={() => setCreating(true)}
          data-testid="autoridades-new"
        >
          <Plus size={14} /> Nueva
        </button>
      </div>
      <div className="card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-200">
            <tr className="border-b border-border">
              <th className="py-2">Corporación</th>
              <th>Responsable</th>
              <th>Teléfono</th>
              <th>Email</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(data ?? []).map((a) => (
              <tr key={a.id} className="border-b border-border/40">
                <td className="py-2">{a.corporacion}</td>
                <td>{a.responsable}</td>
                <td>{a.telefono}</td>
                <td>{a.email ?? ""}</td>
                <td className="text-right">
                  <button className="btn-ghost text-xs" onClick={() => setEditing(a)}>
                    <Edit2 size={12} /> Editar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {creating && (
        <AutoridadFormDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ["autoridades"] });
          }}
        />
      )}
      {editing && (
        <AutoridadFormDialog
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["autoridades"] });
          }}
        />
      )}
    </div>
  );
}

function AutoridadFormDialog({
  row,
  onClose,
  onSaved,
}: {
  row?: Autoridad;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [form, setForm] = useState({
    corporacion: row?.corporacion ?? "",
    area: row?.area ?? "",
    responsable: row?.responsable ?? "",
    telefono: row?.telefono ?? "",
    email: row?.email ?? "",
    notas: row?.notas ?? "",
  });
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!form.corporacion.trim() || !form.responsable.trim() || !form.telefono.trim()) {
      toast.push("Corporación, responsable y teléfono son obligatorios", "error");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        corporacion: form.corporacion.trim(),
        area: form.area || null,
        responsable: form.responsable.trim(),
        telefono: form.telefono.trim(),
        email: form.email || null,
        notas: form.notas || null,
      };
      if (row) await autoridades.update(row.id, body as Partial<Autoridad>);
      else await autoridades.create(body as Omit<Autoridad, "id" | "activo">);
      toast.push("Guardado", "success");
      onSaved();
    } catch {
      toast.push("Error al guardar", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title={row ? "Editar autoridad" : "Nueva autoridad"} onClose={onClose}>
      {(["corporacion", "area", "responsable", "telefono", "email"] as const).map((k) => (
        <input
          key={k}
          className="input"
          placeholder={k}
          value={form[k] ?? ""}
          onChange={(e) => setForm({ ...form, [k]: e.target.value })}
          data-testid={`autoridad-${k}`}
        />
      ))}
      <textarea
        className="input"
        placeholder="Notas"
        rows={2}
        value={form.notas ?? ""}
        onChange={(e) => setForm({ ...form, notas: e.target.value })}
      />
      <div className="flex gap-2">
        <button onClick={onClose} className="btn-ghost flex-1">Cancelar</button>
        <button
          onClick={submit}
          className="btn-primary flex-1"
          disabled={submitting}
          data-testid="autoridad-submit"
        >
          Guardar
        </button>
      </div>
    </Modal>
  );
}

function SimpleCatalogueTab<T extends { id: number; nombre: string; activo: boolean }>(props: {
  queryKey: string;
  list: () => Promise<T[]>;
  create: (body: Record<string, unknown>) => Promise<T>;
  testId: string;
  extraColumns?: { key: keyof T; label: string; render?: (v: unknown) => string }[];
  extraFields?: { key: string; label: string; type: "checkbox" | "text" }[];
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: [props.queryKey], queryFn: props.list });
  const [creating, setCreating] = useState(false);
  const extraColumns = props.extraColumns ?? [];
  const extraFields = props.extraFields ?? [];

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button className="btn-primary" data-testid={`${props.testId}-new`} onClick={() => setCreating(true)}>
          <Plus size={14} /> Nuevo
        </button>
      </div>
      <div className="card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-200">
            <tr className="border-b border-border">
              <th className="py-2">Nombre</th>
              {extraColumns.map((c) => (
                <th key={String(c.key)}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data ?? []).map((r) => (
              <tr key={r.id} className="border-b border-border/40">
                <td className="py-2">{r.nombre}</td>
                {extraColumns.map((c) => {
                  const value = r[c.key];
                  return (
                    <td key={String(c.key)}>
                      {c.render ? c.render(value) : String(value ?? "")}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {creating && (
        <SimpleCreateDialog
          extraFields={extraFields}
          onClose={() => setCreating(false)}
          onSubmit={async (body) => {
            try {
              await props.create(body);
              toast.push("Creado", "success");
              setCreating(false);
              qc.invalidateQueries({ queryKey: [props.queryKey] });
            } catch {
              toast.push("Error al crear", "error");
            }
          }}
        />
      )}
    </div>
  );
}

function MotivosPausaTab() {
  return (
    <SimpleCatalogueTab<MotivoPausa>
      queryKey="motivos-pausa"
      testId="motivos-pausa"
      list={motivosPausa.list}
      create={(body) => motivosPausa.create(body as { nombre: string })}
      extraColumns={[
        { key: "descripcion", label: "Descripción" },
        {
          key: "requiereComentario",
          label: "Requiere comentario",
          render: (v) => (v ? "sí" : "no"),
        },
      ]}
      extraFields={[
        { key: "descripcion", label: "Descripción", type: "text" },
        { key: "requiereComentario", label: "Requiere comentario", type: "checkbox" },
      ]}
    />
  );
}

function MotivosDescarteTab() {
  return (
    <SimpleCatalogueTab<MotivoDescarte>
      queryKey="motivos-descarte"
      testId="motivos-descarte"
      list={motivosDescarte.list}
      create={(body) => motivosDescarte.create(body as { nombre: string })}
      extraColumns={[
        { key: "descripcion", label: "Descripción" },
        {
          key: "requiereEvidencia",
          label: "Evidencia",
          render: (v) => (v ? "sí" : "no"),
        },
        {
          key: "requiereComentario",
          label: "Comentario",
          render: (v) => (v ? "sí" : "no"),
        },
      ]}
      extraFields={[
        { key: "descripcion", label: "Descripción", type: "text" },
        { key: "requiereEvidencia", label: "Requiere evidencia", type: "checkbox" },
        { key: "requiereComentario", label: "Requiere comentario", type: "checkbox" },
      ]}
    />
  );
}

function TiposVehiculoTab() {
  return (
    <SimpleCatalogueTab<TipoVehiculo>
      queryKey="tipos-vehiculo"
      testId="tipos-vehiculo"
      list={tiposVehiculo.list}
      create={(body) => tiposVehiculo.create(body as { nombre: string })}
    />
  );
}

function SimpleCreateDialog({
  extraFields,
  onClose,
  onSubmit,
}: {
  extraFields: { key: string; label: string; type: "checkbox" | "text" }[];
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
}) {
  const [nombre, setNombre] = useState("");
  const [extra, setExtra] = useState<Record<string, unknown>>({});
  const [submitting, setSubmitting] = useState(false);

  return (
    <Modal title="Nuevo" onClose={onClose}>
      <input
        className="input"
        placeholder="Nombre"
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
      />
      {extraFields.map((f) =>
        f.type === "checkbox" ? (
          <label key={f.key} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!extra[f.key]}
              onChange={(e) => setExtra({ ...extra, [f.key]: e.target.checked })}
            />
            {f.label}
          </label>
        ) : (
          <input
            key={f.key}
            className="input"
            placeholder={f.label}
            value={(extra[f.key] as string) ?? ""}
            onChange={(e) => setExtra({ ...extra, [f.key]: e.target.value })}
          />
        )
      )}
      <div className="flex gap-2">
        <button className="btn-ghost flex-1" onClick={onClose}>Cancelar</button>
        <button
          className="btn-primary flex-1"
          disabled={!nombre.trim() || submitting}
          onClick={async () => {
            setSubmitting(true);
            try {
              await onSubmit({ nombre: nombre.trim(), ...extra });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          Guardar
        </button>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/60 p-4">
      <div className="card w-full max-w-md space-y-3">
        <header className="flex items-center justify-between">
          <h2 className="font-semibold">{title}</h2>
          <button className="btn-ghost" onClick={onClose}>×</button>
        </header>
        {children}
      </div>
    </div>
  );
}
